import { useState } from "react";
import axios from "axios";
import { Loader2, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { API, apiError } from "@/lib/api";

// Admin-only blind test: tap every cover you think KLYR made, then reveal.
export const CoverBlindTest = () => {
  const [deck, setDeck] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true); setResult(null); setPicked(new Set());
    try { setDeck((await axios.get(`${API}/admin/cover-test`)).data); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const toggle = (k) => !result && setPicked((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const submit = async () => {
    setBusy(true);
    try { setResult((await axios.post(`${API}/admin/cover-test/score`, { picked_ai: [...picked] })).data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const reveal = Object.fromEntries((result?.reveal || []).map((r) => [r.key, r]));

  return (
    <div data-testid="cover-blind-test" className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="cover-test-start" onClick={start} disabled={busy}
          className="klyr-btn-metal inline-flex items-center gap-2 font-ui text-sm font-semibold rounded-lg px-4 py-2 transition-[filter]">
          {busy && !deck ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-4 h-4" />} {deck ? "Reshuffle" : "Start blind test"}
        </button>
        {deck && !result && (
          <button data-testid="cover-test-submit" onClick={submit} disabled={busy}
            className="font-ui text-sm rounded-lg px-4 py-2 border border-gold-500/30 text-slate-200 hover:border-gold-400/60 transition-colors">
            Reveal ({picked.size} marked as AI)
          </button>
        )}
        <p className="font-read text-sm text-slate-400">Tap every cover you believe is AI-made. Bestseller images are streamed for this private review only.</p>
      </div>
      {result && (
        <div data-testid="cover-test-result" className="rounded-xl border border-gold-500/20 bg-white/[0.03] p-4 font-ui text-sm text-slate-200">
          You marked {result.picked}; {result.correct_ai_picks} of {result.klyr_total} KLYR covers caught, {result.wrong_picks_on_bestsellers} bestsellers wrongly marked.
          {result.precision !== null && <> Precision {Math.round(result.precision * 100)}% (pure guessing ≈ {Math.round(result.chance_precision * 100)}%).</>}
        </div>
      )}
      {deck && (
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-3">
          {deck.cards.map((c) => {
            const r = reveal[c.key];
            return (
              <button key={c.key} data-testid={`cover-card-${c.key}`} onClick={() => toggle(c.key)}
                className={`relative rounded-md overflow-hidden ring-2 transition-[box-shadow,transform] hover:-translate-y-0.5 ${picked.has(c.key) ? "ring-amber-400" : "ring-transparent"}`}>
                <img src={`${API}/admin/cover-test/img/${c.key}`} alt="cover" loading="lazy" className="w-full aspect-[2/3] object-cover bg-navy-900" />
                {picked.has(c.key) && !r && <span className="absolute top-1 right-1 rounded bg-amber-400 text-black font-ui text-[10px] font-bold px-1.5">AI?</span>}
                {r && (
                  <span className={`absolute inset-x-0 bottom-0 px-1.5 py-1 font-ui text-[10px] text-left ${r.kind === "klyr" ? "bg-gold-500/90 text-black" : "bg-black/75 text-white"}`}>
                    {r.kind === "klyr" ? "KLYR" : "Bestseller"} · {r.genre}{r.kind === "klyr" && r.qa ? ` · 100px ${r.qa.pass ? "pass" : "fail"}` : ""}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
